// SPDX-License-Identifier: AGPL-3.0-or-later

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '@intakeform/ui';
import { ChevronRight, Flag, FileText, Inbox } from 'lucide-react';
import { useQuestionnaires } from '../../api/questionnaires.js';
import { useResponses } from '../../api/responses.js';
import { useMe } from '../../api/auth.js';
import { MagicLinkModal } from '../../components/MagicLinkModal.js';
import type { ResponseSummary } from '../../api/types.js';

/**
 * Clinician dashboard — triage landing (Clinical Slate, Pass 7).
 *
 * F-pattern: 4 metric cards across the top, then two columns — a
 * "Needs attention" queue + per-form throughput on the left, a recent
 * activity feed on the right. Everything is derived client-side from
 * the responses + questionnaires the clinician can already read; no new
 * aggregation endpoints. Figures use tabular-nums; accent (~5%) is
 * reserved for queue flags and the single primary action.
 */

interface StatusMeta {
  label: string;
  dot: string;
}
const STATUS: Record<string, StatusMeta> = {
  'in-progress': { label: 'In progress', dot: '--color-neutral' },
  'needs-review': { label: 'Needs review', dot: '--color-warning' },
  'in-review': { label: 'In review', dot: '--color-primary' },
  completed: { label: 'Completed', dot: '--color-info' },
  verified: { label: 'Verified', dot: '--color-success' },
  'entered-in-error': { label: 'Error', dot: '--color-danger' },
};
function statusMeta(s: string): StatusMeta {
  return STATUS[s] ?? { label: s, dot: '--color-neutral' };
}

function patientName(r: ResponseSummary): string {
  return r.patient?.displayName ?? r.patient?.email ?? `Patient ${r.patientId.slice(0, 8)}`;
}

const DAY_MS = 86_400_000;

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

function formatWhen(iso: string | null): string {
  if (iso === null) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

/** Tiny 7-day bar sparkline. `counts` is oldest→newest (length 7). */
function Sparkline({ counts, label }: { counts: number[]; label: string }) {
  const max = Math.max(1, ...counts);
  const w = 7;
  const gap = 3;
  const h = 24;
  return (
    <svg
      className="dash-spark"
      width={counts.length * (w + gap)}
      height={h}
      viewBox={`0 0 ${counts.length * (w + gap)} ${h}`}
      role="img"
      aria-label={label}
    >
      {counts.map((c, i) => {
        const bh = Math.max(2, Math.round((c / max) * (h - 2)));
        return (
          <rect
            key={i}
            x={i * (w + gap)}
            y={h - bh}
            width={w}
            height={bh}
            rx={2}
            fill={`rgb(var(${c > 0 ? '--color-primary' : '--color-border-strong'}))`}
          />
        );
      })}
    </svg>
  );
}

export function DashboardRoute() {
  const navigate = useNavigate();
  const formsQuery = useQuestionnaires();
  const responsesQuery = useResponses({});
  const { data: me } = useMe();
  const [magicLinkOpen, setMagicLinkOpen] = useState(false);

  const responses = useMemo(() => responsesQuery.data ?? [], [responsesQuery.data]);
  const forms = useMemo(() => formsQuery.data ?? [], [formsQuery.data]);
  const titleById = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of forms) m.set(f.id, f.title);
    return m;
  }, [forms]);

  const metrics = useMemo(() => {
    const now = Date.now();
    const todayStart = startOfDay(new Date());
    const weekAgo = now - 7 * DAY_MS;
    let newCount = 0;
    let needsReview = 0;
    let flagged = 0;
    let reviewedToday = 0;
    for (const r of responses) {
      const submitted = r.submittedAt ? new Date(r.submittedAt).getTime() : null;
      if (submitted !== null && submitted >= weekAgo) newCount += 1;
      if (r.status === 'needs-review') needsReview += 1;
      if (r.flagged) flagged += 1;
      if (r.status === 'verified' && new Date(r.updatedAt).getTime() >= todayStart)
        reviewedToday += 1;
    }
    return { newCount, needsReview, flagged, reviewedToday };
  }, [responses]);

  const needsAttention = useMemo(() => {
    return responses
      .filter((r) => r.flagged || r.status === 'needs-review')
      .sort((a, b) => (b.submittedAt ?? '').localeCompare(a.submittedAt ?? ''))
      .slice(0, 6);
  }, [responses]);

  const formThroughput = useMemo(() => {
    // 7 day-start timestamps, oldest → newest.
    const todayStart = startOfDay(new Date());
    const days = Array.from({ length: 7 }, (_, i) => todayStart - (6 - i) * DAY_MS);
    const byForm = new Map<string, number[]>();
    for (const r of responses) {
      if (r.submittedAt === null) continue;
      const t = new Date(r.submittedAt).getTime();
      const idx = days.findIndex((d) => t >= d && t < d + DAY_MS);
      if (idx === -1) continue;
      const arr = byForm.get(r.questionnaireId) ?? new Array<number>(7).fill(0);
      arr[idx] = (arr[idx] ?? 0) + 1;
      byForm.set(r.questionnaireId, arr);
    }
    const activeForms = forms.filter((f) => f.status === 'active');
    const pool = activeForms.length > 0 ? activeForms : forms;
    return pool
      .map((f) => {
        const counts = byForm.get(f.id) ?? new Array<number>(7).fill(0);
        const total = counts.reduce((a, b) => a + b, 0);
        return { id: f.id, title: f.title, counts, total };
      })
      .sort((a, b) => b.total - a.total)
      .slice(0, 6);
  }, [responses, forms]);

  const recentActivity = useMemo(() => {
    return responses
      .filter((r) => r.submittedAt !== null)
      .sort((a, b) => (b.submittedAt ?? '').localeCompare(a.submittedAt ?? ''))
      .slice(0, 8);
  }, [responses]);

  const metricCards = [
    { key: 'new', label: 'New responses (7d)', value: metrics.newCount },
    { key: 'review', label: 'Needs review', value: metrics.needsReview },
    { key: 'flagged', label: 'Flagged', value: metrics.flagged },
    { key: 'reviewed', label: 'Reviewed today', value: metrics.reviewedToday },
  ];

  const loading = responsesQuery.isLoading || formsQuery.isLoading;

  return (
    <div className="dash-page">
      <div className="dash-head">
        <div>
          <p className="dash-eyebrow">Dashboard</p>
          <h1 className="dash-title">Clinic Workspace</h1>
          <p className="dash-greeting muted">
            {greeting()}
            {me?.user?.displayName ? `, ${me.user.displayName}` : ''} — triage at a glance, then
            dive in.
          </p>
        </div>
        <Button variant="primary" onClick={() => setMagicLinkOpen(true)}>
          New magic link
        </Button>
      </div>

      {/* Metric cards */}
      <div className="dash-metrics">
        {metricCards.map((m) => (
          <div key={m.key} data-ui-card="" className="dash-metric">
            <span className="dash-metric-label">{m.label}</span>
            <span className="dash-metric-val">{loading ? '—' : m.value}</span>
          </div>
        ))}
      </div>

      <div className="dash-cols">
        {/* Left: needs attention + forms throughput */}
        <div className="dash-col">
          <section aria-labelledby="dash-attention" data-ui-card="">
            <div className="dash-sec-head">
              <h2 id="dash-attention" data-ui-card-title="">
                Needs attention
              </h2>
              <button
                type="button"
                className="dash-seeall"
                onClick={() => navigate('/responses?filter=flagged')}
              >
                View all <ChevronRight size={14} strokeWidth={1.6} aria-hidden="true" />
              </button>
            </div>
            {loading ? (
              <p className="muted dash-pad">Loading…</p>
            ) : needsAttention.length === 0 ? (
              <div className="dash-empty">
                <Inbox size={22} strokeWidth={1.4} aria-hidden="true" />
                <p>Nothing needs attention. Nice and clear.</p>
              </div>
            ) : (
              <ul className="dash-queue">
                {needsAttention.map((r) => {
                  const meta = statusMeta(r.status);
                  return (
                    <li key={r.id}>
                      <button
                        type="button"
                        className="dash-queue-row"
                        onClick={() => navigate(`/responses/${r.id}`)}
                      >
                        <span
                          className="dash-dot"
                          style={{ background: `rgb(var(${meta.dot}))` }}
                          aria-hidden="true"
                        />
                        <span className="dash-queue-main">
                          <span className="dash-queue-name">{patientName(r)}</span>
                          <span className="dash-queue-form">
                            {titleById.get(r.questionnaireId) ?? 'Intake form'} · {meta.label}
                          </span>
                        </span>
                        {r.flagged ? (
                          <span className="dash-flag" title="Flagged">
                            <Flag
                              size={14}
                              strokeWidth={1.6}
                              fill="currentColor"
                              aria-hidden="true"
                            />
                            <span className="sr-only">Flagged</span>
                          </span>
                        ) : null}
                        <span className="dash-queue-when">{formatWhen(r.submittedAt)}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section aria-labelledby="dash-forms" data-ui-card="">
            <div className="dash-sec-head">
              <h2 id="dash-forms" data-ui-card-title="">
                Your forms
              </h2>
              <button type="button" className="dash-seeall" onClick={() => navigate('/forms')}>
                Manage <ChevronRight size={14} strokeWidth={1.6} aria-hidden="true" />
              </button>
            </div>
            {loading ? (
              <p className="muted dash-pad">Loading…</p>
            ) : formThroughput.length === 0 ? (
              <div className="dash-empty">
                <FileText size={22} strokeWidth={1.4} aria-hidden="true" />
                <p>No forms yet.</p>
              </div>
            ) : (
              <ul className="dash-forms">
                {formThroughput.map((f) => (
                  <li key={f.id} className="dash-form-row">
                    <span className="dash-form-name">{f.title}</span>
                    <Sparkline counts={f.counts} label={`${f.total} this week`} />
                    <span className="dash-form-count">
                      {f.total}
                      <span className="dash-form-count-sub"> / 7d</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        {/* Right: recent activity */}
        <div className="dash-col">
          <section aria-labelledby="dash-activity" data-ui-card="">
            <div className="dash-sec-head">
              <h2 id="dash-activity" data-ui-card-title="">
                Recent activity
              </h2>
            </div>
            {loading ? (
              <p className="muted dash-pad">Loading…</p>
            ) : recentActivity.length === 0 ? (
              <div className="dash-empty">
                <Inbox size={22} strokeWidth={1.4} aria-hidden="true" />
                <p>No submissions yet.</p>
              </div>
            ) : (
              <ul className="dash-activity">
                {recentActivity.map((r) => (
                  <li key={r.id}>
                    <button
                      type="button"
                      className="dash-activity-row"
                      onClick={() => navigate(`/responses/${r.id}`)}
                    >
                      <span
                        className="dash-dot"
                        style={{ background: `rgb(var(${statusMeta(r.status).dot}))` }}
                        aria-hidden="true"
                      />
                      <span className="dash-activity-main">
                        <span className="dash-activity-text">
                          <strong>{patientName(r)}</strong> submitted{' '}
                          {titleById.get(r.questionnaireId) ?? 'an intake form'}
                        </span>
                        <span className="dash-activity-when">{formatWhen(r.submittedAt)}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>

      <MagicLinkModal forms={forms} open={magicLinkOpen} onOpenChange={setMagicLinkOpen} />
    </div>
  );
}
